import logger from '../../log';
import { dispatchPlayerEvent } from '../../events/dispatch';
import {
  ConstructedEvents,
  TechEngagementMethods
} from '../../common/Reporter/constants';
import { CleanupMethod } from '../BaseInteraction';
import {
  AttachableViaOnOff,
  VideoHS,
  VideoJsTech,
  VoidMethod
} from '../interfaces';
import {
  bindTechApiFunction,
  setupTechApiFunction,
  tearDownTechApiFunction,
  TechApi,
  UUFunction
} from '../../bootloader/tech';
import { TrackInfo, TrackType } from '../../reporter/constants';
import { isTrackInfo } from '../../util/getEventForEventMetadata';

const {
  audiochanged: { code: audiochanged },
  captionchanged: { code: captionchanged }
} = ConstructedEvents;

const {
  AUDIOCHANGED,
  BITRATECHANGED: BITRATE,
  CAPTIONCHANGED
} = TechEngagementMethods;

const log = logger.getLogger(__filename);

//#region Interfaces
type MaybeTrackInfo = TrackInfo | undefined;
interface VjsHelperLookupEntry {
  e: number;
  name: string;
  getWrapper: (player: WithVjsTrackInfo) => MaybeVjsTrackListWrapper;
  getMaybeTrackInfo: (player: WithVjsTrackInfo) => MaybeTrackInfo;
}
export const VjsLookupTable: Record<'at' | 'ct', VjsHelperLookupEntry> = {
  at: {
    e: audiochanged,
    name: AUDIOCHANGED,
    getWrapper: p => p.audioTracks?.(),
    getMaybeTrackInfo: p => getTrackInfo(p.audioTracks?.())
  },
  ct: {
    e: captionchanged,
    name: CAPTIONCHANGED,
    getWrapper: p => p.textTracks?.(),
    getMaybeTrackInfo: p => getTrackInfo(p.textTracks?.())
  }
};

export interface VjsTrack {
  kind: string;
  label: string;
  language: string;
}
export interface VjsTextTrack extends VjsTrack {
  mode: 'hidden' | 'disabled' | 'showing';
}
export interface VjsAudioTrack extends VjsTrack {
  enabled: boolean;
}
export type VjsTrackItem = VjsTextTrack | VjsAudioTrack;

type MaybeVjsTrackListWrapper = VjsTrackListWrapper | undefined;
export interface VjsTrackListWrapper {
  on: (n: string, func: UUFunction) => undefined;
  off: (n: string, func: UUFunction) => undefined;
  length: number;
  tracks_: VjsTrackItem[];
  [key: number]: VjsTrackItem;
}

export interface WithVjsTrackInfo extends AttachableViaOnOff {
  textTracks: () => VjsTrackListWrapper;
  audioTracks: () => VjsTrackListWrapper;
}
//#endregion Interfaces
//#region Utilities
/**
 * Get the current bandwidth/bitrate of the video js player
 *
 * NOTE: Exported for unit testing
 *
 * @param vhs - the VideoHS object that contains the playlists.
 * @returns the current bandwidth or undefined if unable to get the bandwidth
 */
export const getBandwidth = (vhs: VideoHS): number | undefined => {
  return vhs?.playlists?.media?.()?.attributes?.BANDWIDTH;
};

/**
 * Determine if a track is currently being used.
 *
 * NOTE: Exported for unit testing
 *
 * @param track - VjsTrack containing track information.
 * @returns true if the track is active
 */
export function trackActive(track: VjsTrack): boolean {
  const a = track as VjsAudioTrack;
  const t = track as VjsTextTrack;
  return a.enabled ?? t.mode === 'showing';
}

/**
 * Find an active track (will return the last active track, but only one track
 * should be active at a time)
 *
 * NOTE: Exported for unit testing
 *
 * @param trackHolder - the video js track holding object
 * @returns the track info of the active track, undefined if there are no
 * tracks available, or an empty track info if none are active
 */
export function getTrackInfo(
  trackHolder?: VjsTrackListWrapper
): TrackInfo | undefined {
  if (!trackHolder) {
    return;
  }
  const tracks = [];
  for (let i = 0; i < trackHolder.length; ++i) {
    tracks.push(trackHolder[i]);
  }
  if (tracks.length === 0) {
    return undefined;
  }
  const info = tracks.reduce((acc: TrackInfo | undefined, item) => {
    if (trackActive(item)) {
      return { lang: item.language, name: item.label };
    }
    return acc;
  }, undefined);
  return info ?? { lang: '', name: '' };
}

/**
 * Generate a listener for an audio or caption track change.
 *
 * NOTE: Exported for unit testing
 *
 * @param type - audio (at) or caption (ct)
 * @param player - the videojs player object
 * @returns a listener for an audio or caption change event
 */
export function generateTrackChangeHandler(
  type: TrackType,
  player: unknown
): VoidMethod {
  const { e, getMaybeTrackInfo } = VjsLookupTable[type];
  return () => {
    const evt = { e, [type]: getMaybeTrackInfo(player as WithVjsTrackInfo) };
    if (isTrackInfo(evt[type])) {
      log.debug('track change', type, evt);
      dispatchPlayerEvent(evt);
    }
  };
}

/**
 * Generates a bitrate change handler and binds to the kollective tech object to
 * allow the Engagement Event to pull the bitrate.
 *
 * NOTE: Exported for unit testing
 *
 * @param tech - the VideoJsTech object
 * @param techApi - the kollective tech object
 * @param teardowns - the list of kollective tech objects that will need to be
 * torn down.
 * @param element - the html media/video element.
 * @returns a bitrate change listener
 */
export function generateBitrateHandler(
  tech: VideoJsTech,
  techApi: TechApi,
  teardownMap: Record<string, CleanupMethod>
): VoidMethod {
  // SRV-1439
  // We need to get the bitrate which there is no event for. Use the
  // 'timeupdate' event to track when bitrate changes.
  // NOTE: this information is only available when we have the
  // metadata available for it (i.e. with a master playlist).
  let lastBitrate: number | undefined = undefined;
  let hasMessaged = false;
  return () => {
    const vhs = tech.vhs || tech.hls;
    if (!vhs) {
      if (!hasMessaged) {
        log.info('No vhs unable to setup bitrate tracker');
        hasMessaged = true;
      }
      return;
    }
    setupTechApiFunction(techApi, BITRATE, () => getBandwidth(vhs), true);
    teardownMap[BITRATE] = () => tearDownTechApiFunction(techApi, BITRATE);

    const b = getBandwidth(vhs);
    if (!b) {
      return;
    }
    const e = ConstructedEvents.playbackbitratechanged.code;
    if (b && b != lastBitrate) {
      log.debug('new timeupdate event, bitrate changed:', { b, e });
      dispatchPlayerEvent({ b, e });
    }
    lastBitrate = b;
  };
}

/**
 * This binds timeupdate to a function that looks for the audioTracks or
 * textTracks object and adds a listener to the 'change' event that posts the
 * reporter event so we can track caption and audio changes (as VideoJs says
 * that it supports player audio and text track changes on the base object but
 * in practice it doesn't always follow through.)
 * Also registers the tear down function
 *
 * @param player - video js player
 * @param type - TrackType
 * @param teardownMethods - the object to register the teardown method.
 * @param handler - the callback that we want to call on the track object
 * on a change
 */
export function bindTrackObjectWhenAvailable(
  p: WithVjsTrackInfo,
  type: TrackType,
  teardownMethods: Record<string, CleanupMethod>
): void {
  const { getWrapper } = VjsLookupTable[type];
  const setupKey = `${type}_setup`;
  const bindTrackObject = () => {
    const info = getWrapper(p);
    if (!info) {
      return;
    }
    const handler = generateTrackChangeHandler(type, p);
    info.on('change', handler);
    teardownMethods[type] = () => info.off('change', handler);
    teardownMethods[setupKey]?.();
    delete teardownMethods[setupKey];
  };
  p.on('timeupdate', bindTrackObject);
  teardownMethods[setupKey] = () => p.off('timeupdate', bindTrackObject);
}

/**
 * Bind a player listener to an event and register its tear down function.
 *
 * NOTE: Exported for unit testing
 *
 * @param player - The VideoJs Player
 * @param name - The name of the change event to bind to
 * @param teardownMethods - the object to register the teardown method.
 * @param handler - The method which will handle the change event
 */
export function bindPlayerMethod(
  player: AttachableViaOnOff,
  name: string,
  teardownMethods: Record<string, CleanupMethod>,
  handler: VoidMethod
): void {
  player.on(name, handler);
  teardownMethods[name] = () => player.off(name, handler);
}

/**
 * Bind the track info retriever to the tech object and set up the teardown
 * method
 *
 * @param p - The VideoJs player
 * @param tech - The kollective tech object
 * @param type - The track type
 * @param teardownMap - The object to register teardown methods
 */
export function bindTechApi(
  p: WithVjsTrackInfo,
  tech: TechApi,
  type: TrackType,
  teardownMap: Record<string, CleanupMethod>
): void {
  const { getMaybeTrackInfo, name } = VjsLookupTable[type];
  bindTechApiFunction(tech, name, teardownMap, () => getMaybeTrackInfo(p));
}
//#endregion Utilities

/**
 * Add listeners to the VideoJs player's events so that we can track the changes
 * to the tracks and bitrate.
 *
 * @param player - the video js player
 * @param tech - the VideoJsTech object
 * @param techApi - the kollective tech object to attach functions to.
 * @returns cleanup method which removes the listeners
 */
const attachVideoJsHandlers = (
  player: AttachableViaOnOff,
  tech: VideoJsTech,
  techApi: TechApi
): CleanupMethod => {
  const p = player as unknown as WithVjsTrackInfo;
  const cleanupMap: Record<string, CleanupMethod> = {};

  // Create handlers to send reporter events on change
  const onaudiochange = generateTrackChangeHandler('at', player);
  const ontextchange = generateTrackChangeHandler('ct', player);
  // NOTE: the techApi is bound when the bitrate handler is working.
  const onbitratechange = generateBitrateHandler(tech, techApi, cleanupMap);

  // Bind the tech functions so that the Engagement event works
  bindTechApi(p, techApi, 'at', cleanupMap);
  bindTechApi(p, techApi, 'ct', cleanupMap);

  // Bind the videojs audio or text track object when it's available.
  bindTrackObjectWhenAvailable(p, 'at', cleanupMap);
  bindTrackObjectWhenAvailable(p, 'ct', cleanupMap);

  // Bind the listener to the event
  bindPlayerMethod(p, 'audiotrackchange', cleanupMap, onaudiochange);
  bindPlayerMethod(p, 'texttrackchange', cleanupMap, ontextchange);
  bindPlayerMethod(p, 'timeupdate', cleanupMap, onbitratechange);

  return () => Object.values(cleanupMap).forEach(p => p());
};

export default attachVideoJsHandlers;
